import { z } from 'zod';
import { type Instant, type PlainDate, daysUntil } from '../time/index.js';

/**
 * Stock state rules (vault Core Model, accepted change 4; plan §5.2). A rule is an ordered
 * list of states a lot passes through as its best-before approaches, e.g.
 * fresh → day-old → clearance → removed. Each state carries the business's rules: sellable?
 * on which channels? at what markdown? usable as an ingredient?
 *
 * Order: the **first** state is where stock starts (it has no threshold and covers every
 * day further out than the second state's threshold). Each later state starts
 * `fromDaysBeforeBestBefore` local calendar days before the best-before date (0 = on the
 * best-before day, −1 = the day after) and lasts until the next state starts. Thresholds
 * strictly decrease. The **last** state must start on or after the best-before day
 * (threshold ≤ 0), so every rule says what happens to stock at expiry.
 * @module
 */

/** State keys: lowercase, digits and `_`, e.g. `fresh`, `day_old`. */
export const STATE_KEY_RE = /^[a-z][a-z0-9_]{0,31}$/;

/** zod schema for a state key. */
export const stateKeySchema = z
  .string()
  .regex(STATE_KEY_RE, 'State keys are lowercase, e.g. "fresh" or "day_old"');

/** One state of a {@link StateRule}. */
export const stateRuleStateSchema = z.object({
  key: stateKeySchema,
  /**
   * Local days before best-before at which this state starts; `null` only for the first
   * state (it covers everything from production).
   */
  fromDaysBeforeBestBefore: z.int().min(-3650).max(3650).nullable(),
  /** Counts as available and may be sold. */
  sellable: z.boolean(),
  /**
   * Channel ids this state may be offered on. Empty = none (eligibility defaults off).
   * Must be empty when the state is not sellable.
   */
  channels: z.array(z.uuid()),
  /** Markdown in basis points (2500 = 25% off). */
  markdownBp: z.int().min(0).max(10_000),
  /** May be consumed by production. */
  usableAsIngredient: z.boolean(),
});
/** One state of a rule. */
export type StateRuleState = z.infer<typeof stateRuleStateSchema>;

/** A problem with a rule's state list. `index` is the state's position. */
export type StateRuleIssue =
  | { code: 'no_states' }
  | { code: 'duplicate_key'; index: number; key: string }
  | { code: 'first_state_has_threshold'; index: 0 }
  | { code: 'missing_threshold'; index: number }
  | { code: 'not_strictly_ordered'; index: number }
  | { code: 'does_not_reach_expiry'; index: number }
  | { code: 'channels_on_unsellable_state'; index: number }
  | { code: 'duplicate_channel'; index: number; channel: string };

/**
 * Checks the ordering rules (see the module note). Shape is checked by
 * {@link stateRuleStatesSchema}; this checks what the states mean together.
 */
export function validateStateRule(states: readonly StateRuleState[]): StateRuleIssue[] {
  const issues: StateRuleIssue[] = [];
  if (states.length === 0) return [{ code: 'no_states' }];
  const keys = new Set<string>();
  let previous: number | null = null;
  states.forEach((s, index) => {
    if (keys.has(s.key)) issues.push({ code: 'duplicate_key', index, key: s.key });
    keys.add(s.key);
    const from = s.fromDaysBeforeBestBefore;
    if (index === 0) {
      if (from !== null) issues.push({ code: 'first_state_has_threshold', index: 0 });
    } else if (from === null) {
      issues.push({ code: 'missing_threshold', index });
    } else {
      if (previous !== null && from >= previous) {
        issues.push({ code: 'not_strictly_ordered', index });
      }
      previous = from;
    }
    if (!s.sellable && s.channels.length > 0) {
      issues.push({ code: 'channels_on_unsellable_state', index });
    }
    const seen = new Set<string>();
    for (const channel of s.channels) {
      if (seen.has(channel)) issues.push({ code: 'duplicate_channel', index, channel });
      seen.add(channel);
    }
  });
  // A single open-ended state covers expiry by definition; otherwise the last threshold
  // must be on or after the best-before day.
  const lastIndex = states.length - 1;
  const lastFrom = (states[lastIndex] as StateRuleState).fromDaysBeforeBestBefore;
  if (lastIndex > 0 && lastFrom !== null && lastFrom > 0) {
    issues.push({ code: 'does_not_reach_expiry', index: lastIndex });
  }
  return issues;
}

/** Human-readable text for a {@link StateRuleIssue}. */
export function describeStateRuleIssue(issue: StateRuleIssue): string {
  switch (issue.code) {
    case 'no_states':
      return 'A state rule needs at least one state.';
    case 'duplicate_key':
      return `State ${issue.index + 1}: the key "${issue.key}" is used twice.`;
    case 'first_state_has_threshold':
      return 'The first state starts at production, so it has no "days before best-before".';
    case 'missing_threshold':
      return `State ${issue.index + 1}: say how many days before best-before it starts.`;
    case 'not_strictly_ordered':
      return `State ${issue.index + 1} must start closer to best-before than the state before it.`;
    case 'does_not_reach_expiry':
      return 'The last state must start on or after the best-before day.';
    case 'channels_on_unsellable_state':
      return `State ${issue.index + 1} is not sellable, so it cannot be offered on channels.`;
    case 'duplicate_channel':
      return `State ${issue.index + 1} lists the same channel twice.`;
  }
}

/** zod schema for a rule's ordered states, with the ordering rules applied. */
export const stateRuleStatesSchema = z.array(stateRuleStateSchema).superRefine((states, ctx) => {
  for (const issue of validateStateRule(states)) {
    ctx.addIssue({
      code: 'custom',
      message: describeStateRuleIssue(issue),
      path: 'index' in issue ? [issue.index] : [],
      params: { rule: issue.code },
    });
  }
});

/** A state rule's states. */
export type StateRule = readonly StateRuleState[];

/** Result of {@link stateOn}. */
export interface StateOnResult {
  state: StateRuleState;
  /** Position of `state` in the rule. */
  index: number;
  /** Local calendar days until best-before (0 on the day, negative once past). */
  daysUntilBestBefore: number;
}

/**
 * The state a lot with `bestBefore` is in at `instant`, judged by the **local** calendar
 * date in `timeZone` (an SAST midnight is a new day, whatever UTC says). Throws
 * `RangeError` on a rule that fails {@link validateStateRule}.
 */
export function stateOn(
  bestBefore: PlainDate,
  instant: Instant,
  timeZone: string,
  rule: StateRule,
): StateOnResult {
  const issues = validateStateRule(rule);
  if (issues.length > 0) {
    throw new RangeError(`Invalid state rule: ${issues.map(describeStateRuleIssue).join(' ')}`);
  }
  const days = daysUntil(bestBefore, instant, timeZone);
  let index = 0;
  rule.forEach((s, i) => {
    const from = s.fromDaysBeforeBestBefore;
    if (from !== null && days <= from) index = i;
  });
  return { state: rule[index] as StateRuleState, index, daysUntilBestBefore: days };
}
